/**
 * Chat design-store catalog. Mirrors the other widget catalogs but renders a
 * LIST of recent chat messages (not a single value). Recreated 1:1 from the
 * Claude Design `Chat Widgets.html` handoff (10 variants), cropped from the
 * original 1920×1080 scenes (the `translate(110 200)` panel group) to a tight
 * 560×680 panel and re-parametrised so the live chat feed fills the rows.
 *
 * Each design is now PURE DATA (a {@link SerializableDesign}): the panel chrome
 * lives in `frame`, the per-message styling in `list` (spliced at `{{ROWS}}`).
 * The generic {@link renderDesign} turns the data + live messages into SVG — no
 * per-design `build()` JS is transported or run, so a published design is safe.
 * `renderChatDesignSvg(design, messages)` is the single entry point the Store
 * thumbnail, the config preview and the live Pixi renderer all call.
 *
 * SECURITY: chat text + usernames are USER-CONTROLLED. Every interpolated string
 * is XML-escaped (after truncation) and every colour sanitised to a `#rrggbb`
 * literal inside {@link renderDesign}'s single choke-point — so user content can
 * never inject SVG, for both first-party and community-published designs.
 *
 * Conventions (shared with the other catalogs): viewBox `0 0 cardW cardH`, no
 * width/height attrs (the raster injects them); every defs/gradient/filter/
 * pattern/`<g id>` id is prefixed with the design id so panels inline safely.
 *
 * Known caveats / deliberate decisions (intentional v1 scope):
 *  - Remote avatar images, inline emotes, and platform mod/sub BADGES are not
 *    embedded — rows use the design's colored avatar dots + plain text (the SVG
 *    raster can't load cross-origin images synchronously). Author colour still
 *    distinguishes users.
 *  - Single-line designs (twitch/cyber/anime) flow "name + message" inline via
 *    `<tspan>` rather than the mock's hardcoded per-row x-offsets. Those offsets
 *    were measured for the specific sample names; with real variable-length
 *    usernames a fixed offset would overlap long names / gap short ones, so
 *    inline flow is the correct rendering for live data. DO NOT "restore" the
 *    fixed offsets — it reads as more faithful to the static mock but breaks on
 *    real chat.
 *  - The header online-count shows a live "● EN VIVO"/"● LIVE" indicator (or the
 *    design's status dot) instead of the mock's fixed "312".
 *  - CJK/emoji in messages fall back to a system face for fonts (VT323 /
 *    Audiowide / …) lacking those glyphs.
 */

import { renderDesign } from "../entities/designs/renderDesign";
import { getInstalledDesign } from "../entities/designs/installedRegistry";
import type { SerializableDesign } from "../entities/designs/serializableDesign";

export type ChatDesignCategory = "chat";

/** One chat row, already mapped from a `ChatMessage`. */
export interface ChatLine {
  name: string;
  text: string;
  /** Author/platform colour (`#rrggbb`); sanitised, falls back per design. */
  color?: string;
  /** `HH:MM` for designs that show a timestamp (e.g. brutalist). */
  time?: string;
}

/**
 * A chat design is a {@link SerializableDesign} (always `widgetType:"chat"` with
 * a `list`), plus the catalog UI metadata. `list.maxRows` is the panel's row
 * budget (the latest N messages are laid out).
 */
export interface ChatDesign extends SerializableDesign {
  widgetType: "chat";
  category: ChatDesignCategory;
  /**
   * Max message rows this panel shows (mirrors `list.maxRows`). Kept at the top
   * level so the live `ChatDesignRenderer` can key its visible-window dedup off
   * `design.maxRows` without reaching into the serializable `list`.
   */
  maxRows: number;
}

// ── 01 · Dark Minimal ──────────────────────────────────────────────────────
const DARK_PAL = [
  "#a07dff",
  "#7fbfff",
  "#ffb86b",
  "#6be3a0",
  "#ff7a7a",
  "#ff8ed4",
];
const darkDesign: ChatDesign = {
  id: "dark",
  name: "Dark Minimal",
  subtitle: "Rounded rows",
  widgetType: "chat",
  category: "chat",
  description:
    "Dotted charcoal panel with rounded message rows, colored usernames and avatar dots.",
  swatches: ["#0d0d12", "#1a1a22", "#a07dff", "#7fbfff"],
  cardWidth: 560,
  cardHeight: 680,
  fonts: ["Inter", "DM Mono"],
  maxRows: 7,
  frame: `<svg viewBox="0 0 560 680" xmlns="http://www.w3.org/2000/svg">
  <defs><pattern id="dark-chd-dots" width="22" height="22" patternUnits="userSpaceOnUse"><circle cx="2" cy="2" r="1.1" fill="#33333d" opacity=".6"/></pattern></defs>
  <rect width="560" height="680" rx="22" fill="#0d0d12" stroke="#2a2a33" stroke-width="1.5"/>
  <rect width="560" height="680" rx="22" fill="url(#dark-chd-dots)"/>
  <text x="32" y="56" font-family="Inter, sans-serif" font-weight="700" font-size="24" fill="#f5f5f5" letter-spacing="2">CHAT EN VIVO</text>
  <text x="528" y="56" text-anchor="end" font-family="DM Mono, monospace" font-size="16" fill="#3ad96b">● EN VIVO</text>
  <line x1="32" y1="78" x2="528" y2="78" stroke="#26262f"/>
  <g font-family="Inter, sans-serif">{{ROWS}}</g>
  <text x="32" y="660" font-family="DM Mono, monospace" font-size="14" fill="#6b7280" letter-spacing="4">› escribiendo…</text>
</svg>`,
  list: {
    maxRows: 7,
    baselineY: 110,
    rowHeight: 64,
    palette: DARK_PAL,
    rowSlots: {
      color: { kind: "color" },
      name: { kind: "text", cap: 16 },
      text: { kind: "text", cap: 40 },
    },
    row: `<g transform="translate(32 {{ROW_Y}})"><rect width="496" height="56" rx="14" fill="#15151d"/><circle cx="34" cy="28" r="14" fill="{{color}}"/><text x="58" y="24" font-weight="700" font-size="17" fill="{{color}}">@{{name}}</text><text x="58" y="46" font-size="17" fill="#cfd6e2">{{text}}</text></g>`,
  },
};

// ── 02 · Twitch Purple ─────────────────────────────────────────────────────
const TWITCH_PAL = [
  "#ff7a85",
  "#7fd1ff",
  "#ffca7a",
  "#74e0a3",
  "#ff7ad0",
  "#c8a0ff",
  "#ffb86b",
];
const twitchDesign: ChatDesign = {
  id: "twitch",
  name: "Twitch Purple",
  subtitle: "Native feel",
  widgetType: "chat",
  category: "chat",
  description:
    'Native-Twitch chat column with colored names and a "Stream Chat" header bar.',
  swatches: ["#18181b", "#9146ff", "#ffffff", "#bf94ff"],
  cardWidth: 560,
  cardHeight: 680,
  fonts: ["Inter"],
  maxRows: 10,
  // Inline name+message via <tspan> (no fixed x-offset) — correct for
  // variable-length usernames; see the header note on single-line designs.
  frame: `<svg viewBox="0 0 560 680" xmlns="http://www.w3.org/2000/svg">
  <rect width="560" height="680" rx="10" fill="#18181b"/>
  <rect width="560" height="56" rx="10" fill="#1f1f23"/>
  <rect y="40" width="560" height="16" fill="#1f1f23"/>
  <text x="280" y="36" text-anchor="middle" font-family="Inter, sans-serif" font-weight="700" font-size="18" fill="#efeff1" letter-spacing="2">STREAM CHAT</text>
  <line x1="0" y1="56" x2="560" y2="56" stroke="#2a2a2e"/>
  <g font-family="Inter, sans-serif">{{ROWS}}</g>
  <g transform="translate(28 596)"><rect width="504" height="48" rx="6" fill="#0e0e10" stroke="#3a3a40" stroke-width="1"/><text x="16" y="30" font-family="Inter, sans-serif" font-size="15" fill="#5a5a60">Enviá un mensaje</text><rect x="430" y="8" width="62" height="32" rx="4" fill="#9146ff"/><text x="461" y="30" text-anchor="middle" font-family="Inter, sans-serif" font-weight="700" font-size="13" fill="#fff">Chat</text></g>
</svg>`,
  list: {
    maxRows: 10,
    baselineY: 96,
    rowHeight: 46,
    palette: TWITCH_PAL,
    rowSlots: {
      color: { kind: "color" },
      name: { kind: "text", cap: 16 },
      text: { kind: "text", cap: 32 },
    },
    row: `<text x="28" y="{{ROW_Y}}" font-size="18"><tspan font-weight="700" fill="{{color}}">{{name}}</tspan><tspan fill="#efeff1">: {{text}}</tspan></text>`,
  },
};

// ── 03 · Y2K Aqua Gel ──────────────────────────────────────────────────────
const y2kDesign: ChatDesign = {
  id: "y2k",
  name: "Y2K Aqua Gel",
  subtitle: "Glossy bubbles",
  widgetType: "chat",
  category: "chat",
  description:
    "Glossy aqua chat bubbles with chrome highlights and a green online indicator.",
  swatches: ["#003a7a", "#1c8fff", "#8acfff", "#5aff8a"],
  cardWidth: 560,
  cardHeight: 680,
  fonts: ["Audiowide", "DM Mono", "Inter"],
  maxRows: 6,
  frame: `<svg viewBox="0 0 560 680" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="y2k-chd-hd" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#cfeaff"/><stop offset="100%" stop-color="#1c8fff"/></linearGradient>
    <linearGradient id="y2k-chd-bub" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stop-color="#eaf6ff"/><stop offset="100%" stop-color="#bfe2ff"/></linearGradient>
  </defs>
  <rect width="560" height="680" rx="30" fill="#012a55" stroke="#fff" stroke-width="3"/>
  <rect x="10" y="10" width="540" height="60" rx="22" fill="url(#y2k-chd-hd)"/>
  <ellipse cx="280" cy="26" rx="240" ry="8" fill="#fff" opacity=".7"/>
  <text x="30" y="52" font-family="Audiowide, sans-serif" font-size="22" fill="#012a55">CHATROOM</text>
  <g transform="translate(490 40)"><circle r="9" fill="#012a55"/><circle r="5" fill="#5aff8a"/></g>
  <g font-family="Audiowide, sans-serif" font-size="16">{{ROWS}}</g>
  <g transform="translate(30 564)"><rect width="500" height="48" rx="24" fill="url(#y2k-chd-hd)" stroke="#fff" stroke-width="2"/><ellipse cx="250" cy="10" rx="210" ry="5" fill="#fff" opacity=".7"/><text x="20" y="31" font-family="DM Mono, monospace" font-size="14" fill="#fff">type a message…</text></g>
</svg>`,
  list: {
    maxRows: 6,
    baselineY: 96,
    rowHeight: 76,
    rowSlots: {
      name: { kind: "text", cap: 18 },
      text: { kind: "text", cap: 32 },
    },
    row: `<g transform="translate(30 {{ROW_Y}})"><rect width="500" height="64" rx="20" fill="url(#y2k-chd-bub)"/><ellipse cx="250" cy="12" rx="220" ry="5" fill="#fff" opacity=".7"/><text x="20" y="30" fill="#1c8fff">@{{name}}</text><text x="20" y="52" font-family="Inter, sans-serif" font-size="16" fill="#012a55">{{text}}</text></g>`,
  },
};

// ── 04 · Cyberpunk Neon ────────────────────────────────────────────────────
const CYBER_PAL = ["#00f0ff", "#ff2e88", "#f5d019"];
const cyberDesign: ChatDesign = {
  id: "cyber",
  name: "Cyberpunk Neon",
  subtitle: "Netchat terminal",
  widgetType: "chat",
  category: "chat",
  description:
    'Chamfered HUD chat with neon usernames, "› " prompts and a glowing input line.',
  swatches: ["#080010", "#00f0ff", "#ff2e88", "#f5d019"],
  cardWidth: 560,
  cardHeight: 680,
  fonts: ["DM Mono", "VT323"],
  maxRows: 11,
  frame: `<svg viewBox="0 0 560 680" xmlns="http://www.w3.org/2000/svg">
  <defs><filter id="cyber-chd-g"><feGaussianBlur stdDeviation="2"/><feMerge><feMergeNode/><feMergeNode in="SourceGraphic"/></feMerge></filter><pattern id="cyber-chd-scan" width="100%" height="3" patternUnits="userSpaceOnUse"><rect width="100%" height="1" fill="#00f0ff" opacity=".06"/></pattern></defs>
  <g filter="url(#cyber-chd-g)">
    <polygon points="0,30 30,0 530,0 560,30 560,650 530,680 30,680 0,650" fill="#080010" stroke="#ff2e88" stroke-width="2.5"/>
    <polygon points="12,36 40,12 520,12 548,36 548,644 520,668 40,668 12,644" fill="none" stroke="#00f0ff" stroke-width="1.5" opacity=".7"/>
    <rect x="12" y="12" width="536" height="656" fill="url(#cyber-chd-scan)"/>
    <text x="30" y="52" font-family="DM Mono, monospace" font-size="18" fill="#f5d019" letter-spacing="6">▮ NETCHAT · チャット</text>
    <text x="530" y="52" text-anchor="end" font-family="DM Mono, monospace" font-size="14" fill="#ff2e88">● LIVE</text>
    <g>{{ROWS}}</g>
    <g transform="translate(30 600)"><rect width="500" height="44" fill="none" stroke="#00f0ff" stroke-width="1.5"/><text x="16" y="30" font-family="VT323, monospace" font-size="24" fill="#88e8d8">CMD ›_</text></g>
  </g>
</svg>`,
  list: {
    maxRows: 11,
    baselineY: 100,
    rowHeight: 38,
    palette: CYBER_PAL,
    rowSlots: {
      color: { kind: "color" },
      name: { kind: "text", cap: 12 },
      text: { kind: "text", cap: 26 },
    },
    row: `<text x="30" y="{{ROW_Y}}" font-family="VT323, monospace" font-size="24"><tspan fill="{{color}}">› @{{name}}</tspan><tspan fill="#cfeff5"> {{text}}</tspan></text>`,
  },
};

// ── 05 · Editorial ─────────────────────────────────────────────────────────
const editorialDesign: ChatDesign = {
  id: "editorial",
  name: "Editorial",
  subtitle: "Letters column",
  widgetType: "chat",
  category: "chat",
  description:
    'Cream newspaper "cartas de lectores" column with italic names and serif body.',
  swatches: ["#f5efe2", "#1a1815", "#c8a050", "#8a3a2a"],
  cardWidth: 560,
  cardHeight: 680,
  fonts: ["Cormorant Garamond", "DM Mono", "Inter"],
  maxRows: 6,
  frame: `<svg viewBox="0 0 560 680" xmlns="http://www.w3.org/2000/svg">
  <rect width="560" height="680" fill="#f5efe2"/>
  <line x1="24" y1="24" x2="536" y2="24" stroke="#1a1815" stroke-width="3"/>
  <line x1="24" y1="32" x2="536" y2="32" stroke="#1a1815" stroke-width="1"/>
  <text x="280" y="74" text-anchor="middle" font-family="Cormorant Garamond, serif" font-weight="600" font-size="40" fill="#1a1815" letter-spacing="2">CARTAS DEL CHAT</text>
  <text x="280" y="100" text-anchor="middle" font-family="DM Mono, monospace" font-size="12" fill="#8a3a2a" letter-spacing="8">— SECCIÓN DE LECTORES —</text>
  <line x1="24" y1="118" x2="536" y2="118" stroke="#1a1815" stroke-width="1"/>
  <g font-family="Cormorant Garamond, serif" fill="#1a1815">{{ROWS}}</g>
  <line x1="24" y1="600" x2="536" y2="600" stroke="#1a1815" stroke-width="1"/>
  <text x="32" y="636" font-family="DM Mono, monospace" font-size="13" fill="#8a3a2a" letter-spacing="6">CONTINÚA EN PÁGINA SIGUIENTE · @eriol</text>
</svg>`,
  list: {
    maxRows: 6,
    baselineY: 156,
    rowHeight: 70,
    rowSlots: {
      name: { kind: "text", cap: 20 },
      text: { kind: "text", cap: 44 },
    },
    row: `<g transform="translate(32 {{ROW_Y}})"><text font-style="italic" font-weight="600" font-size="22">@{{name}} escribe:</text><text y="28" font-family="Inter, sans-serif" font-size="16">"{{text}}"</text></g>`,
  },
};

// ── 06 · Pixel Arcade ──────────────────────────────────────────────────────
const PIXEL_PAL = ["#48cae4", "#ffb703", "#ff3d4d"];
const pixelDesign: ChatDesign = {
  id: "pixel",
  name: "Pixel Arcade",
  subtitle: "RPG textbox",
  widgetType: "chat",
  category: "chat",
  description:
    "8-bit dialog box with Press Start 2P names, sprite avatars and chunky rows.",
  swatches: ["#1d2a3a", "#ffb703", "#48cae4", "#ff3d4d"],
  cardWidth: 560,
  cardHeight: 680,
  fonts: ["Press Start 2P"],
  maxRows: 6,
  frame: `<svg viewBox="0 0 560 680" xmlns="http://www.w3.org/2000/svg" shape-rendering="crispEdges">
  <rect width="560" height="680" fill="#48cae4"/>
  <rect x="8" y="8" width="544" height="664" fill="#1d2a3a"/>
  <rect x="16" y="16" width="528" height="648" fill="#0a121c"/>
  <g fill="#ffb703"><rect width="24" height="24"/><rect x="536" width="24" height="24"/><rect y="656" width="24" height="24"/><rect x="536" y="656" width="24" height="24"/></g>
  <g transform="translate(40 -16)"><rect width="220" height="32" fill="#ff3d4d"/><rect x="6" y="6" width="208" height="20" fill="#1d2a3a"/><text x="110" y="22" text-anchor="middle" font-family="Press Start 2P, monospace" font-size="12" fill="#ffb703">CHAT LOG</text></g>
  <g font-family="Press Start 2P, monospace" font-size="13" fill="#fff">{{ROWS}}</g>
  <g transform="translate(40 560)"><rect width="480" height="48" fill="#000" stroke="#48cae4" stroke-width="3"/><text x="16" y="32" font-family="Press Start 2P, monospace" font-size="11" fill="#48cae4">TYPE...▮</text></g>
</svg>`,
  list: {
    maxRows: 6,
    baselineY: 80,
    rowHeight: 70,
    palette: PIXEL_PAL,
    rowSlots: {
      color: { kind: "color" },
      // name + text are uppercased by the entry fn BEFORE the cap+escape.
      name: { kind: "text", cap: 12 },
      text: { kind: "text", cap: 20 },
      bodyY: { kind: "number" },
    },
    row: `<g transform="translate(40 {{ROW_Y}})"><rect x="0" y="-18" width="20" height="20" fill="{{color}}"/><text x="30" fill="#48cae4">{{name}}</text></g><text x="40" y="{{bodyY}}" font-size="12" fill="#fff">{{text}}</text>`,
  },
};

// ── 07 · Cottagecore ───────────────────────────────────────────────────────
const cottageDesign: ChatDesign = {
  id: "cottage",
  name: "Cottagecore",
  subtitle: "Stitched notes",
  widgetType: "chat",
  category: "chat",
  description:
    "Linen panel with cross-stitch border, italic Cormorant names and flower bullets.",
  swatches: ["#f0e4cc", "#6b4a2a", "#b85a4a", "#5a7a4a"],
  cardWidth: 560,
  cardHeight: 680,
  fonts: ["Cormorant Garamond", "DM Mono", "Inter"],
  maxRows: 6,
  frame: `<svg viewBox="0 0 560 680" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <pattern id="cottage-chd-linen" width="4" height="4" patternUnits="userSpaceOnUse"><rect width="4" height="4" fill="#f7eed5"/><rect width="1" height="4" fill="#d8c8a8" opacity=".55"/><rect y="2" width="4" height="1" fill="#d8c8a8" opacity=".4"/></pattern>
    <pattern id="cottage-chd-st" width="24" height="24" patternUnits="userSpaceOnUse"><path d="M5 5 L19 19 M19 5 L5 19" stroke="#b85a4a" stroke-width="2.5" stroke-linecap="round"/></pattern>
    <g id="cottage-chd-fl"><circle r="7" fill="#ff9da8"/><circle cx="-7" cy="-3" r="5" fill="#ff9da8"/><circle cx="7" cy="-3" r="5" fill="#ff9da8"/><circle cx="-4" cy="6" r="5" fill="#ff9da8"/><circle cx="4" cy="6" r="5" fill="#ff9da8"/><circle r="3" fill="#ffd060"/></g>
  </defs>
  <rect width="560" height="680" fill="url(#cottage-chd-linen)" stroke="#6b4a2a" stroke-width="2"/>
  <rect width="560" height="24" fill="url(#cottage-chd-st)"/>
  <rect y="656" width="560" height="24" fill="url(#cottage-chd-st)"/>
  <rect width="24" height="680" fill="url(#cottage-chd-st)"/>
  <rect x="536" width="24" height="680" fill="url(#cottage-chd-st)"/>
  <text x="280" y="76" text-anchor="middle" font-family="Cormorant Garamond, serif" font-style="italic" font-weight="600" font-size="36" fill="#2a1a08">notitas del chat</text>
  <text x="280" y="102" text-anchor="middle" font-family="DM Mono, monospace" font-size="12" fill="#5a7a4a" letter-spacing="8">— amigues en línea —</text>
  <g fill="#2a1a08">{{ROWS}}</g>
  <text x="50" y="616" font-family="DM Mono, monospace" font-size="13" fill="#b85a4a" letter-spacing="4">escribí algo lindo… 🌱</text>
</svg>`,
  list: {
    maxRows: 6,
    baselineY: 150,
    rowHeight: 70,
    rowSlots: {
      name: { kind: "text", cap: 18 },
      text: { kind: "text", cap: 40 },
    },
    row: `<g transform="translate(50 {{ROW_Y}})"><use href="#cottage-chd-fl"/><text x="24" y="6" font-style="italic" font-weight="600" font-size="22">{{name}}</text><text x="24" y="32" font-family="Inter, sans-serif" font-size="15">{{text}}</text></g>`,
  },
};

// ── 08 · Brutalist ─────────────────────────────────────────────────────────
const brutalistDesign: ChatDesign = {
  id: "brutalist",
  name: "Brutalist",
  subtitle: "Ledger log",
  widgetType: "chat",
  category: "chat",
  description:
    "Black slab with orange edge, mono timestamps, bold names and a hard-ruled log.",
  swatches: ["#1a1a1a", "#f5f5f5", "#ff5a2a", "#c8c2b8"],
  cardWidth: 560,
  cardHeight: 680,
  fonts: ["DM Mono", "Space Grotesk"],
  maxRows: 6,
  frame: `<svg viewBox="0 0 560 680" xmlns="http://www.w3.org/2000/svg">
  <rect width="560" height="680" fill="#1a1a1a"/>
  <rect width="24" height="680" fill="#ff5a2a"/>
  <text x="48" y="56" font-family="DM Mono, monospace" font-size="16" fill="#ff5a2a" letter-spacing="10">// CHAT.LOG</text>
  <line x1="48" y1="76" x2="528" y2="76" stroke="#3a3a3a"/>
  <g font-family="DM Mono, monospace" font-size="16">{{ROWS}}</g>
  <line x1="48" y1="600" x2="528" y2="600" stroke="#3a3a3a"/>
  <text x="48" y="640" font-family="DM Mono, monospace" font-size="14" fill="#ff5a2a" letter-spacing="6">&gt; _</text>
</svg>`,
  list: {
    maxRows: 6,
    baselineY: 120,
    rowHeight: 76,
    rowSlots: {
      time: { kind: "text", cap: 5 },
      // name is uppercased by the entry fn BEFORE the cap+escape.
      name: { kind: "text", cap: 16 },
      text: { kind: "text", cap: 44 },
      bodyY: { kind: "number" },
    },
    row: `<g transform="translate(48 {{ROW_Y}})"><text fill="#5a5a5a">{{time}}</text><text x="84" font-family="Space Grotesk, sans-serif" font-weight="700" fill="#f5f5f5">@{{name}}</text></g><text x="48" y="{{bodyY}}" font-family="Space Grotesk, sans-serif" font-size="17" fill="#c8c2b8">{{text}}</text>`,
  },
};

// ── 09 · Anime VHS ─────────────────────────────────────────────────────────
// Cyan tracking ghost reaches x≈567.5 (rect x=6 w=560 stroke 3) → card 568.
const ANIME_PAL = ["#ff7ad0", "#7ad0ff"];
const animeDesign: ChatDesign = {
  id: "anime",
  name: "Anime VHS",
  subtitle: "チャット",
  widgetType: "chat",
  category: "chat",
  description:
    "Pink double-stroke panel with cyan tracking ghost, katakana corner and VT323 lines.",
  swatches: ["#0a0218", "#ff7ad0", "#7ad0ff", "#fff7c2"],
  cardWidth: 568,
  cardHeight: 680,
  fonts: ["VT323", "DM Mono"],
  maxRows: 10,
  // Inline name+message via <tspan> (no fixed x-offset) — correct for
  // variable-length usernames; see the header note on single-line designs.
  frame: `<svg viewBox="0 0 568 680" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <filter id="anime-chd-rgb"><feOffset in="SourceGraphic" dx="-3" dy="0" result="r"/><feColorMatrix in="r" values="1 0 0 0 1   0 0 0 0 0.5   0 0 0 0 0.8   0 0 0 1 0" result="rr"/><feOffset in="SourceGraphic" dx="3" dy="0" result="c"/><feColorMatrix in="c" values="0 0 0 0 0.5   0 0 0 0 0.9   0 0 0 0 1   0 0 0 1 0" result="cc"/><feMerge><feMergeNode in="rr"/><feMergeNode in="cc"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    <pattern id="anime-chd-scan" width="100%" height="3" patternUnits="userSpaceOnUse"><rect width="100%" height="1" fill="#ff7ad0" opacity=".12"/></pattern>
  </defs>
  <g filter="url(#anime-chd-rgb)">
    <rect x="6" y="0" width="560" height="680" fill="none" stroke="#7ad0ff" stroke-width="3" opacity=".55"/>
    <rect width="560" height="680" fill="#0a0218" stroke="#ff7ad0" stroke-width="4"/>
    <rect x="10" y="10" width="540" height="660" fill="none" stroke="#7ad0ff" stroke-width="1.5" opacity=".55"/>
    <rect width="560" height="680" fill="url(#anime-chd-scan)"/>
    <g><rect width="220" height="36" fill="#ff7ad0"/><text x="14" y="26" font-family="DM Mono, monospace" font-size="16" fill="#0a0218" letter-spacing="4">チャット · CHAT</text></g>
    <text x="540" y="28" text-anchor="end" font-family="VT323, monospace" font-size="24" fill="#7ad0ff">● LIVE</text>
    <g>{{ROWS}}</g>
    <g transform="translate(28 600)"><rect width="504" height="44" fill="#1a0a2a" stroke="#7ad0ff" stroke-width="2"/><text x="14" y="30" font-family="VT323, monospace" font-size="24" fill="#ff7ad0">メッセージ ▮</text></g>
  </g>
</svg>`,
  list: {
    maxRows: 10,
    baselineY: 100,
    rowHeight: 42,
    palette: ANIME_PAL,
    rowSlots: {
      color: { kind: "color" },
      name: { kind: "text", cap: 12 },
      text: { kind: "text", cap: 24 },
    },
    row: `<text x="28" y="{{ROW_Y}}" font-family="VT323, monospace" font-size="26"><tspan fill="{{color}}">@{{name}}</tspan><tspan fill="#fff7c2"> {{text}}</tspan></text>`,
  },
};

// ── 10 · Liquid Glass ──────────────────────────────────────────────────────
const LIQUID_PAL = ["#9ad4ff", "#7fbfff", "#bfe3ff", "#cfeeff"];
const liquidDesign: ChatDesign = {
  id: "liquid",
  name: "Liquid Glass",
  subtitle: "Frosted bubbles",
  widgetType: "chat",
  category: "chat",
  description:
    "Frosted glass chat over a cobalt gradient with translucent bubbles and soft highlights.",
  swatches: ["#0a1230", "#1c3a7a", "#9ad4ff", "#ffffff"],
  cardWidth: 560,
  cardHeight: 680,
  fonts: ["Space Grotesk", "DM Mono", "Inter"],
  maxRows: 6,
  frame: `<svg viewBox="0 0 560 680" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <radialGradient id="liquid-chd-bg" cx="40%" cy="20%" r="100%"><stop offset="0%" stop-color="#2a4a9a"/><stop offset="60%" stop-color="#0a1230"/><stop offset="100%" stop-color="#020615"/></radialGradient>
    <radialGradient id="liquid-chd-bokeh" cx="50%" cy="50%" r="50%"><stop offset="0%" stop-color="#9ad4ff" stop-opacity=".6"/><stop offset="100%" stop-color="#9ad4ff" stop-opacity="0"/></radialGradient>
    <filter id="liquid-chd-blur"><feGaussianBlur stdDeviation="22"/></filter>
  </defs>
  <rect width="560" height="680" rx="28" fill="url(#liquid-chd-bg)" stroke="rgba(255,255,255,.22)" stroke-width="1.5"/>
  <g filter="url(#liquid-chd-blur)" opacity=".7"><circle cx="120" cy="120" r="120" fill="url(#liquid-chd-bokeh)"/><circle cx="460" cy="540" r="140" fill="url(#liquid-chd-bokeh)"/></g>
  <text x="32" y="56" font-family="Space Grotesk, sans-serif" font-weight="600" font-size="24" fill="#fff" letter-spacing="2">CHAT</text>
  <text x="528" y="56" text-anchor="end" font-family="DM Mono, monospace" font-size="15" fill="#9ad4ff">● EN VIVO</text>
  <g font-family="Inter, sans-serif">{{ROWS}}</g>
  <g transform="translate(32 560)"><rect width="496" height="48" rx="24" fill="rgba(255,255,255,.12)" stroke="rgba(255,255,255,.35)" stroke-width="1"/><text x="20" y="30" font-family="DM Mono, monospace" font-size="14" fill="#9ad4ff">escribí un mensaje…</text></g>
</svg>`,
  list: {
    maxRows: 6,
    baselineY: 96,
    rowHeight: 72,
    palette: LIQUID_PAL,
    rowSlots: {
      color: { kind: "color" },
      name: { kind: "text", cap: 16 },
      text: { kind: "text", cap: 38 },
    },
    row: `<g transform="translate(32 {{ROW_Y}})"><rect width="496" height="60" rx="20" fill="rgba(255,255,255,.10)" stroke="rgba(255,255,255,.25)" stroke-width="1"/><rect x="16" y="14" width="32" height="32" rx="16" fill="{{color}}"/><text x="60" y="30" font-weight="700" font-size="16" fill="{{color}}">@{{name}}</text><text x="60" y="50" font-size="15" fill="#e8f4ff">{{text}}</text></g>`,
  },
};

export const CHAT_DESIGNS: ChatDesign[] = [
  darkDesign,
  twitchDesign,
  y2kDesign,
  cyberDesign,
  editorialDesign,
  pixelDesign,
  cottageDesign,
  brutalistDesign,
  animeDesign,
  liquidDesign,
];

/** Minimal subset of `ChatMessage` the catalog reads. */
export interface ChatRenderMessage {
  authorName: string;
  text: string;
  authorColor?: string;
  timestamp?: number;
}

const pad2 = (n: number): string => String(n).padStart(2, "0");
const fmtTime = (ts: number | undefined): string | undefined => {
  if (!ts) return undefined;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return undefined;
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

/** Designs whose row markup uppercases the username before cap+escape. */
const UPPERCASE_NAME = new Set(["pixel", "brutalist"]);
/** Designs whose row markup uppercases the message text before cap+escape. */
const UPPERCASE_TEXT = new Set(["pixel"]);
/** Designs that show an `HH:MM` timestamp per row (falling back to "--:--"). */
const SHOWS_TIME = new Set(["brutalist"]);
/**
 * Campos de fila que vienen del chat (terceros): `renderDesign` los escapa
 * SIEMPRE, aunque la metadata `rowSlots` de un diseño de la comunidad declare
 * `escape:false` (el servidor no valida esa metadata).
 */
const CHAT_UNTRUSTED_FIELDS: readonly string[] = ["name", "text", "time"];

/**
 * Render a design's panel SVG for the given recent messages. Takes the latest
 * `design.list.maxRows` (chronological) and maps each message to a list item;
 * {@link renderDesign} truncates + XML-escapes every text field and sanitises
 * colours at its single choke-point.
 */
export function renderChatDesignSvg(
  design: ChatDesign,
  messages: ChatRenderMessage[],
): string {
  const list = design.list;
  const upperName = UPPERCASE_NAME.has(design.id);
  const upperText = UPPERCASE_TEXT.has(design.id);
  const showsTime = SHOWS_TIME.has(design.id);
  const baselineY = list?.baselineY ?? 0;
  const rowHeight = list?.rowHeight ?? 0;

  // Pre-slice to the row budget so the per-item `bodyY` index matches the
  // 0-based row index the renderer uses for `{{ROW_Y}}` (it re-slices to
  // maxRows internally — a no-op once we've already trimmed).
  const recent = list ? messages.slice(-list.maxRows) : messages;

  const items = recent.map((m, i) => {
    const name = m.authorName ?? "";
    const text = m.text ?? "";
    const item: Record<string, string | number | undefined> = {
      name: upperName ? name.toUpperCase() : name,
      text: upperText ? text.toUpperCase() : text,
      color: m.authorColor,
    };
    if (showsTime) item.time = fmtTime(m.timestamp) ?? "--:--";
    // Secondary message-body element (pixel/brutalist) sits 26px below the row
    // origin; the generic renderer only exposes ROW_Y, so derive it per item.
    item.bodyY = baselineY + i * rowHeight + 26;
    return item;
  });

  return renderDesign(design, { items, untrusted: CHAT_UNTRUSTED_FIELDS });
}

/**
 * Una adaptación por payload instalado: la identidad del `ChatDesign` devuelto
 * es estable mientras el registro tenga el mismo payload y cambia cuando se
 * re-guarda bajo el mismo id de fila — así el monitor (`watchRegistry`) y los
 * memos del editor detectan el reemplazo por referencia.
 */
const ADAPTED_COMMUNITY = new WeakMap<SerializableDesign, ChatDesign>();

export function getChatDesign(id: string | undefined): ChatDesign | undefined {
  if (!id) {
    return undefined;
  }
  const local = CHAT_DESIGNS.find((d) => d.id === id);
  if (local) {
    return local;
  }
  // Not a shipped first-party id — fall back to an INSTALLED community chat
  // design (hydrated into the in-memory registry by `useDesignLibrary`) so a
  // design the user applied (`options.designId` = a backend id) renders live
  // via `renderChatDesignSvg` exactly like a first-party one. The registry
  // already constrains the lookup to `widgetType: "chat"`. Community payloads
  // are `SerializableDesign` and don't carry the catalog-only top-level
  // `maxRows`, so derive it from the list budget for the live renderer.
  const installed = getInstalledDesign(id, "chat");
  if (!installed) {
    return undefined;
  }
  const cached = ADAPTED_COMMUNITY.get(installed);
  if (cached) {
    return cached;
  }
  const adapted: ChatDesign = {
    ...installed,
    widgetType: "chat",
    category: "chat",
    maxRows: installed.list?.maxRows ?? 0,
  };
  ADAPTED_COMMUNITY.set(installed, adapted);
  return adapted;
}
